import * as React from 'react';
import { AspectRatio } from 'react-aspect-ratio';

import { Box, Typography, styled } from '@mui/material';

import LazyAvatar, { LazyAvatarProps } from '../../../components/Avatar/LazyAvatar/LazyAvatar';
import PlayIcon from '../../../components/PlayIcon/PlayIcon';
import { VenueReservationContentItemV2 } from '../../../graphql/API';
import Icons from '../../../themes/icons';

export const RequirementContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  top: 12,
  left: 12,
  zIndex: 99,
});

const AvatarBody: React.FC<LazyAvatarProps> = (props) => (
  <Box sx={{ flexShrink: 0, borderRadius: '50%', overflow: 'hidden' }}>
    <LazyAvatar {...props} sx={{ width: '35px', height: '35px', borderRadius: '50%' }} />
  </Box>
);

type ContentProps = {
  onClick: React.MouseEventHandler;
  src: string;
  video?: string;
  userAvatar: string;
  name: string;
  offerTitle?: string | null;
  data?: VenueReservationContentItemV2;
  variant?: 'grid' | 'list';
};

const Content: React.FC<ContentProps> = ({
  onClick,
  src,
  userAvatar,
  name,
  offerTitle,
  video,
  data,
  variant = 'grid',
}) => {
  const isList = variant === 'list';

  const mediaBlock = (
    <Box sx={{ position: 'relative', flexShrink: 0 }}>
      {data?.ispost && (
        <RequirementContainer>
          {data?.postOnInstagramStories || data?.postOnInstagramStories3x || data?.postOnInstagramStories2x ? (
            <Icons.Content1StoryIcon />
          ) : data?.postOnInstagramFeed ? (
            <Icons.ContentFeedIcon />
          ) : data?.postOnInstagramReels ? (
            <Icons.ContentReelIcon />
          ) : data?.postOnTikTokVideo ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: '34px',
                height: '34px',
                background: 'rgba(255, 255, 255, 0.90)',
                borderRadius: '50%',
              }}
            >
              <Icons.TiktokIcon width={16} height={20} />
            </Box>
          ) : null}
        </RequirementContainer>
      )}
      {isList ? (
        <Box
          component="img"
          src={src}
          alt=""
          sx={{
            width: 80,
            height: 80,
            borderRadius: '11.14px',
            objectFit: 'cover',
          }}
        />
      ) : (
        <AspectRatio ratio="0.8">
          <Box
            component="img"
            src={src}
            alt=""
            sx={{
              borderRadius: '11.14px',
              objectFit: 'cover',
              width: '100%',
              height: '100%',
            }}
          />
        </AspectRatio>
      )}
      {video && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            top: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PlayIcon />
        </Box>
      )}
    </Box>
  );

  const metaBlock = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        paddingTop: isList ? 0 : '15px',
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        ...(isList && { flex: 1, minWidth: 0 }),
      }}
    >
      <AvatarBody variant="circular" src={userAvatar} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          fontWeight={600}
          fontSize="13px"
          sx={{ fontFamily: 'Poppins', fontWeight: 500, color: '#111111', fontSize: '14px' }}
          noWrap
        >
          {name}
        </Typography>
        {offerTitle != null && offerTitle !== '' && (
          <Typography
            fontSize="14px"
            color="text.secondary"
            sx={{ fontFamily: 'Poppins', color: 'rgba(17, 17, 17, 0.7)', fontSize: '14px' }}
            noWrap
          >
            {offerTitle}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        cursor: 'pointer',
        ...(isList && {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 2,
          p: 1.5,
          borderRadius: '8px',
          border: '1px solid #EFEEEE',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
        }),
      }}
      onClick={onClick}
    >
      {mediaBlock}
      {metaBlock}
    </Box>
  );
};

export default Content;
